import { OfflinePayment, OfflinePaymentStatus } from 'utils/api/models/billingPaymentTrack/offlinePayment';
import { useHistory } from 'react-router';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/manageGroupsStyle';
import { URL_PATHS } from '../commons/constants';
import React, { useEffect, useMemo, useState } from 'react';
import { BodyS, HeadingMBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import { AccountCell, TextCell } from 'components/Cells/Cells';
import Dropdown from 'components/Dropdown/Dropdown';
import { Add, MoreVert, InsertDriveFileOutlined } from '@material-ui/icons';
import Badge from 'components/Badge/Badge';
import UploadedFilesDialog from 'views/BillingPaymentTrack/subcomponents/UploadedFilesDialog';
import DetailsRequestDialog from 'views/BillingPaymentTrack/subcomponents/DetailsRequestDialog';
import moment from 'moment';
import { getLanguage } from 'utils/language/language';
import { BankItem } from 'utils/api/models/billingPaymentTrack/bankItem';
import { BillingPaymentTrackService } from 'services/billingPaymentTrack/billingPaymentTrack';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import Business from '@material-ui/icons/Business';

interface IManagePayments {
	payments: OfflinePayment[];
	match: {
		path: string;
	};
	accounts: SingleCorpAccount[];
	selectedAccount: ISelectedAccount | null;
	setSelectedAccount: React.Dispatch<React.SetStateAction<ISelectedAccount | null>>;
}

const dialogs = {
	NONE: 'none',
	UPLOADED_FILES: 'uploadedFiles',
	REQUEST_DETAILS: 'requestDetails',
};

// @ts-ignore
const useStyles = makeStyles(styles);

const ManagePayments = ({ payments, match, accounts, selectedAccount, setSelectedAccount }: IManagePayments) => {
	const [searchValue, setSearchValue] = useState<string>('');
	const [currentDialog, setCurrentDialog] = useState<string>(dialogs.NONE);
	const [currentPayment, setCurrentPayment] = useState<OfflinePayment | undefined>();

	const history = useHistory();
	const classes = useStyles();

	const [bankItems, setBankItems] = useState<BankItem[]>([]);
	const lang = getLanguage();

	useEffect(() => {
		const subscription = BillingPaymentTrackService.getBanks(lang).subscribe((resp) =>
			setBankItems(resp.bankItems)
		);

		return () => {
			subscription.unsubscribe();
		};
	}, [lang]);

	const bankName = (bankCode: string) => bankItems.find((bankItem) => bankItem.code === bankCode)?.name;

	const addNewPayment = () => {
		history.push(`${match.path}/${URL_PATHS.REGISTER_NEW_PAYMENT}`);
	};

	const hideDialog = () => {
		setCurrentDialog(dialogs.NONE);
	};

	const accountsMap = useMemo(
		() => accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {}),
		[accounts]
	);

	const filteredPayments: OfflinePayment[] = useMemo(() => {
		return payments.filter(({ ticketExternalId }) =>
			(ticketExternalId || '').toLowerCase().includes(searchValue.toLowerCase())
		);
	}, [payments, searchValue]);

	return (
		<div className={classes.manageGroupsWrapper}>
			<div className={classes.manageGroupsDescriptionWrapper}>
				<HeadingMBold color="secondary-light" disableMargin>
					{translate('billing.payment.track.status.list.heading')}
				</HeadingMBold>
				<BodyS color="disabled-light" disableMargin>
					{translate('billing.payment.track.status.list.description')}
				</BodyS>
			</div>
			<AccountFilterTable
				accounts={accounts}
				setAccount={(value) => setSelectedAccount(value)}
				selectedAccount={selectedAccount}
				filterHeader={''}
				filtersValue={{}}
				filters={[]}
				onFilterChange={() => {}}
				data={filteredPayments}
				selectPanel={{}}
				columns={[
					{
						Header: translate('billing.payment.track.status.ticket.id'),
						accessor: 'ticketExternalId',
						sortable: true,
						Cell: function TicketExternalIdCell({ original }: { original: OfflinePayment }) {
							return (
								<TextCell
									text={original.ticketExternalId || translate('offline.payment.no.data')}
									bold
								/>
							);
						},
					},
					!selectedAccount?.id && {
						Header: translate('table.organization.name'),
						Cell: ({ original }: { original: OfflinePayment }) => {
							const account = accountsMap[original.createdBy];
							return account ? account.accountName : null;
						},
						accessor: 'account.accountName',
					},
					{
						Header: translate('billing.payment.track.status.tracking.number'),
						accessor: 'trackingNumber',
						sortable: true,
						Cell: function TrackingNumberCell({ original }: { original: OfflinePayment }) {
							return (
								<TextCell text={original.trackingNumber || translate('offline.payment.no.data')} bold />
							);
						},
					},
					{
						Header: translate('billing.payment.track.payment.type.form'),
						accessor: 'paymentType',
						sortable: true,
						Cell: function PaymentTypeCell({ original }: { original: OfflinePayment }) {
							return (
								<TextCell
									text={
										original.paymentType
											? translate(`billing.payment.track.payment.type.${original.paymentType}`)
											: translate('offline.payment.no.data')
									}
								/>
							);
						},
					},
					{
						Header: translate('billing.payment.track.status.dates'),
						accessor: 'paymentDate',
						sortable: true,
						Cell: function DatesCell({ original }: { original: OfflinePayment }) {
							return (
								<TextCell
									text={
										original.approvalDate
											? moment(original.approvalDate).format('DD.MM.YYYY')
											: translate('offline.payment.no.data')
									}
								/>
							);
						},
					},
					{
						Header: translate('billing.payment.track.status.bank.name'),
						accessor: 'bankCode',
						sortable: true,
						Cell: function BankNameCell({ original }: { original: OfflinePayment }) {
							return (
								<TextCell
									text={bankName(original.bankCode) || translate('offline.payment.no.data')}
									bold
								/>
							);
						},
					},
					{
						Header: translate('billing.payment.track.status.paid.amount'),
						accessor: 'totalAmountPaid',
						sortable: true,
						Cell: function PaidAmountCell({ original }: { original: OfflinePayment }) {
							return (
								<TextCell
									text={
										original.totalAmountPaid
											? translate('price', original.totalAmountPaid.toFixed(3))
											: translate('offline.payment.no.data')
									}
								/>
							);
						},
					},
					{
						Header: translate('billing.payment.track.status.files'),
						sortable: false,
						Cell: function StatusCell({ original }: { original: OfflinePayment }) {
							return (
								<>
									<InsertDriveFileOutlined />
									<TextCell
										text={original.attachments.length + 'x' || translate('offline.payment.no.data')}
										bold
									/>
								</>
							);
						},
					},
					{
						Header: translate('billing.payment.track.status.status'),
						accessor: 'paymentStatus',
						sortable: true,
						Cell: function StatusCell({ original }: { original: OfflinePayment }) {
							var color: types.theme.badgeColor = 'rose';
							switch (original.paymentStatus) {
								case OfflinePaymentStatus.NEW:
									color = 'rose';
									break;
								case OfflinePaymentStatus.IN_PROGRESS:
									color = 'rose';
									break;
								case OfflinePaymentStatus.REJECTED:
									color = 'danger';
									break;
								case OfflinePaymentStatus.ERROR:
									color = 'danger';
									break;
								case OfflinePaymentStatus.RESOLVED:
									color = 'success';
									break;
								case OfflinePaymentStatus.DONE:
									color = 'success';
									break;
							}

							return (
								<Badge color={color} filled key={original.paymentId}>
									{translate(`billing.payment.track.status.${original.paymentStatus}`) ||
										translate('offline.payment.no.data')}
								</Badge>
							);
						},
					},
					{
						width: 84,
						Cell: function DropDownCell({ original }: { original: OfflinePayment }) {
							return (
								<div className={classes.moreWrapper}>
									<Dropdown
										menu={<MoreVert />}
										items={[
											{
												text: translate('billing.payment.track.uploaded.files.heading'),
												onClick: () => {
													setCurrentDialog(dialogs.UPLOADED_FILES);
													setCurrentPayment(original);
												},
											},
											{
												text: translate('billing.payment.track.details.request.dialog.title'),
												onClick: () => {
													setCurrentDialog(dialogs.REQUEST_DETAILS);
													setCurrentPayment(original);
												},
											},
										]}
										anchorOrigin={{
											vertical: 'bottom',
											horizontal: 'right',
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: 'right',
										}}
									/>
								</div>
							);
						},
					},
				].filter(Boolean)}
				setSearchInputValue={(value) => setSearchValue(value)}
				searchInputValue={searchValue}
				buttons={{
					text: (
						<>
							<Add />
							{translate('billing.payment.track.status.new.request.button')}
						</>
					),
					onClick: addNewPayment,
					action: 'billing-payment-track:add-new-offline-payment',
					buttonProps: {
						color: 'primary-light',
						className: classes.singleButton,
					},
				}}
				loading={false}
			/>
			<UploadedFilesDialog
				hideDialog={hideDialog}
				isOpen={currentDialog === dialogs.UPLOADED_FILES}
				offlinePayment={currentPayment}
			/>
			<DetailsRequestDialog
				hideDialog={hideDialog}
				isOpen={currentDialog === dialogs.REQUEST_DETAILS}
				offlinePayment={currentPayment}
				bankName={bankName(currentPayment?.bankCode || '')}
			/>
		</div>
	);
};

export default ManagePayments;
